import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import apiClient from "utils/apiClient";
import {
  ACTOR_ID_HEADER_KEY,
  CLIENT_NAME_HEADER_KEY,
  CLIENT_NAME_HEADER_VALUE,
  PAYMENT_SERVICE_BASE_URL,
} from "constants";

const BASE_URL = `${PAYMENT_SERVICE_BASE_URL}/api/v1/payments/refunds`;

const toMoney = (cents) => {
  const value = Number(cents);
  if (!Number.isFinite(value)) return 0;
  return value / 100;
};

export default function useRefunds() {
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);

  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";

    if (err?.response) {
      message =
        err.response.data?.message ||
        err.response.data?.error ||
        `Failed to ${action}: ${err.response.statusText}`;
    } else if (err?.request) {
      message =
        "No response from the server. Please check your internet connection.";
    } else if (err?.message) {
      message = err.message;
    }

    toast.error(message);
    // eslint-disable-next-line no-console
    console.error(`${action} error`, err);
  };

  const fetchRefundSummary = useCallback(async (transactionId) => {
    if (!transactionId) return null;
    setLoadingSummary(true);
    try {
      const res = await apiClient.get(`${BASE_URL}/transaction/${transactionId}`);
      setSummary(res.data || null);
      return res.data;
    } catch (err) {
      handleAxiosErrors(err, "fetch refund summary");
      return null;
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  const createRefund = useCallback(
    async ({
      transactionId,
      refundType,
      amount,
      reason,
      actorId,
      correlationId,
    }) => {
      if (!transactionId) throw new Error("Missing transactionId");
      if (!refundType) throw new Error("Missing refundType");
      if (!reason || String(reason).trim() === "") {
        throw new Error("Refund reason is required");
      }

      setLoadingCreate(true);
      try {
        const payload = {
          transactionId,
          refundType,
          amount:
            refundType === "PARTIAL"
              ? Number(amount)
              : undefined,
          reason: String(reason).trim(),
        };

        const headers = {
          ...(actorId ? { [ACTOR_ID_HEADER_KEY]: actorId } : {}),
          [CLIENT_NAME_HEADER_KEY]: CLIENT_NAME_HEADER_VALUE,
          ...(correlationId ? { "O-CORRELATION-ID": correlationId } : {}),
        };

        const res = await apiClient.post(BASE_URL, payload, { headers });
        return res.data;
      } catch (err) {
        handleAxiosErrors(err, "create refund");
        throw err;
      } finally {
        setLoadingCreate(false);
      }
    },
    [],
  );

  return {
    summary,
    loadingSummary,
    loadingCreate,
    fetchRefundSummary,
    createRefund,
    toMoney,
  };
}
